// TODO-KPI-91 — AGIRLIKLI_ORT eşleme önizlemesi: motor değer ve ağırlık kalemlerini SIRA ile eşler
// (i. değer kalemi ↔ i. ağırlık kalemi; sıra = set içi kalem sırası, shared `compareKpiSiblings`).
// Editör (FormulaEditor) yazılan ifadenin eşlemesini, formül penceresi (FormulaPopover) hücrenin
// çiftlerini değerleriyle gösterir. Sayı uyuşmazlığı metni sunucu 422'siyle aynı (shared formatlayıcı).

import {
  compareKpiSiblings,
  formatWeightedAvgArityMessage,
  listWeightedAvgRefs,
  parseFormula,
  type KpiResolvedCell,
  type KpiResolvedGrid,
  type KpiResolvedIndicator,
} from "@mahir/shared";
import { readCell, type GridColumn } from "./kpi-grid-nav";

export interface WavgChild {
  code: string;
  label: string;
}

/** kod → alt kalemler (set/katalog sırasıyla). `labelOf` verilmezse `label` alanı. */
export function buildChildrenByCode<
  T extends {
    id: string;
    code: string;
    parentId: string | null;
    orderIndex: number;
    label: string;
  },
>(items: readonly T[], labelOf: (i: T) => string = (i) => i.label): Map<string, WavgChild[]> {
  const byId = new Map(items.map((i) => [i.id, i]));
  const kids = new Map<string, T[]>();
  for (const i of items) {
    if (!i.parentId || !byId.has(i.parentId)) continue;
    const list = kids.get(i.parentId) ?? [];
    list.push(i);
    kids.set(i.parentId, list);
  }
  const out = new Map<string, WavgChild[]>();
  for (const [parentId, list] of kids) {
    const parent = byId.get(parentId)!;
    const sorted = [...list].sort((a, b) =>
      compareKpiSiblings(
        { orderIndex: a.orderIndex, label: labelOf(a) },
        { orderIndex: b.orderIndex, label: labelOf(b) },
      ),
    );
    out.set(
      parent.code,
      sorted.map((c) => ({ code: c.code, label: labelOf(c) })),
    );
  }
  return out;
}

export interface WavgPreviewGroup {
  code: string;
  weightCode: string;
  /** satır = sıra; kısa kalan taraf null (ortağı yok) */
  pairs: { value: WavgChild | null; weight: WavgChild | null }[];
  /** sayılar eşit değilse sunucu 422 metninin aynısı */
  mismatch: string | null;
}

/**
 * Editör önizlemesi: ifadedeki her AGIRLIKLI_ORT çağrısı için sıralı çiftler. Ayrıştırılamayan
 * ifade, bilinmeyen kod (alt kalem haritasında da etiket haritasında da yok) ya da iki tarafı da
 * alt kalemsiz çağrı → grup yok (boş başlık gösterilmez).
 */
export function wavgEditorPreview(
  expression: string,
  childrenByCode: ReadonlyMap<string, readonly WavgChild[]>,
  labelsByCode?: ReadonlyMap<string, string>,
): WavgPreviewGroup[] {
  const parsed = parseFormula(expression);
  if (!parsed.ok) return [];
  const known = (c: string): boolean => childrenByCode.has(c) || (labelsByCode?.has(c) ?? false);
  return listWeightedAvgRefs(parsed.ast)
    .filter((r) => known(r.code) && known(r.weightCode))
    .filter(
      (r) =>
        (childrenByCode.get(r.code)?.length ?? 0) +
          (childrenByCode.get(r.weightCode)?.length ?? 0) >
        0,
    )
    .map((r) => {
      const values = childrenByCode.get(r.code) ?? [];
      const weights = childrenByCode.get(r.weightCode) ?? [];
      const n = Math.max(values.length, weights.length);
      const pairs = Array.from({ length: n }, (_, i) => ({
        value: values[i] ?? null,
        weight: weights[i] ?? null,
      }));
      const mismatch =
        values.length === weights.length
          ? null
          : formatWeightedAvgArityMessage(
              { ...r, valueCount: values.length, weightCount: weights.length },
              (c) => labelsByCode?.get(c),
            );
      return { code: r.code, weightCode: r.weightCode, pairs, mismatch };
    });
}

export interface WavgPopoverPair {
  valueLabel: string;
  valueCell: KpiResolvedCell | undefined;
  weightLabel: string;
  weightCell: KpiResolvedCell | undefined;
}

/**
 * Formül penceresi: hücrenin AGIRLIKLI_ORT çiftleri (aynı kolon). Görünür kalem sayıları eşit
 * değilse grup gösterilmez (portalda kapalı kalemler grid'de yoktur — yanıltıcı eşleme olurdu);
 * gerçek uyuşmazlık hücre `failure`'ından gerekçe satırı olarak okunur.
 */
export function wavgPopoverPairs(
  ind: KpiResolvedIndicator,
  grid: KpiResolvedGrid,
  col: GridColumn,
): WavgPopoverPair[][] {
  const active =
    ind.formulas.find((f) => f.id === ind.activeFormulaId) ?? ind.formulas.find((f) => f.isActive);
  if (!active) return [];
  const parsed = parseFormula(active.expression);
  if (!parsed.ok) return [];
  const refs = listWeightedAvgRefs(parsed.ast);
  if (refs.length === 0) return [];
  const kidsOf = (code: string): KpiResolvedIndicator[] | null => {
    const target = grid.indicators.find((i) => i.code === code);
    if (!target) return null;
    return grid.indicators.filter((i) => i.parentId === target.id).sort(compareKpiSiblings);
  };
  const groups: WavgPopoverPair[][] = [];
  for (const r of refs) {
    const values = kidsOf(r.code);
    const weights = kidsOf(r.weightCode);
    if (!values || !weights || values.length === 0 || values.length !== weights.length) continue;
    groups.push(
      values.map((v, i) => ({
        valueLabel: v.label,
        valueCell: readCell(v, col),
        weightLabel: weights[i]!.label,
        weightCell: readCell(weights[i]!, col),
      })),
    );
  }
  return groups;
}
